import React, { useState } from "react";
import "./DocumentPreview.css";

const DocumentPreview = ({
  documentData,
  qualityScore,
  recommendations = [],
  onClose,
  onDownload,
  entityName,
}) => {
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState("markdown");

  const getQualityScoreColor = (score) => {
    if (score >= 80) return "quality-score-high";
    if (score >= 50) return "quality-score-medium";
    return "quality-score-low";
  };

  const getQualityScoreLabel = (score) => {
    if (score >= 80) return "High";
    if (score >= 50) return "Medium";
    return "Low";
  };

  const getPriorityBadgeClass = (priority) => {
    return `recommendation-priority-badge ${priority.toLowerCase()}`;
  };

  const handleDirectLink = (link) => {
    if (link && link.startsWith("/")) {
      window.location.href = link;
    }
  };

  const renderHighlightedContent = (content) => {
    // Content may contain HTML spans for highlighting
    // We'll render it as HTML since it includes our highlight spans
    // In production, you might want to use a proper HTML sanitizer like DOMPurify
    return (
      <div
        className="document-content"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  };

  return (
    <div className="document-preview-container">
      {/* Quality Score Header */}
      <div className="document-quality-header">
        <div className="quality-score-container">
          <span className="quality-score-label">Document Quality:</span>
          <span
            className={`quality-score-value ${getQualityScoreColor(qualityScore)}`}
          >
            {Math.round(qualityScore)}%
          </span>
          <span
            className={`quality-score-label ${getQualityScoreColor(qualityScore)}`}
          >
            [{getQualityScoreLabel(qualityScore)}]
          </span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            onClick={() => setShowRecommendations(!showRecommendations)}
            style={{
              padding: "0.5rem 1rem",
              background: "#374151",
              color: "white",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer",
              fontSize: "0.875rem",
            }}
          >
            {showRecommendations ? "Hide" : "Show"} Recommendations (
            {recommendations.length})
          </button>
          {onDownload && (
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              style={{
                padding: "0.5rem",
                background: "#374151",
                color: "white",
                border: "none",
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
              }}
            >
              <option value="markdown">Markdown</option>
              <option value="docx">Word (DOCX)</option>
              <option value="pdf">PDF</option>
            </select>
          )}
          {onDownload && (
            <button
              onClick={() => onDownload(selectedFormat)}
              style={{
                padding: "0.5rem 1rem",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: "500",
              }}
            >
              Download
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: "0.5rem 1rem",
                background: "#6b7280",
                color: "white",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer",
                fontSize: "0.875rem",
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Recommendations Panel */}
      {showRecommendations && recommendations.length > 0 && (
        <div className="recommendations-panel">
          <div className="recommendations-header">
            <h3 style={{ margin: 0, color: "#f3f4f6", fontSize: "1rem" }}>
              Recommendations ({recommendations.length})
            </h3>
          </div>
          <div className="recommendations-list">
            {recommendations.map((rec, index) => (
              <div
                key={index}
                className={`recommendation-card recommendation-priority-${rec.priority}`}
              >
                <div className="recommendation-header">
                  <span className={getPriorityBadgeClass(rec.priority)}>
                    {rec.priority}
                  </span>
                  <span
                    style={{
                      color: "#d1d5db",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                    }}
                  >
                    {rec.type === "attribute_update"
                      ? `Update ${rec.attribute}`
                      : rec.type === "relationship_add"
                      ? `Add Relationship`
                      : rec.type === "entity_add"
                      ? `Add ${rec.target_entity_type}`
                      : "Improve Section"}
                  </span>
                </div>
                <div className="recommendation-reason">{rec.reason}</div>
                {rec.impact && (
                  <div className="recommendation-impact">
                    <strong>Impact:</strong> {rec.impact}
                  </div>
                )}
                {rec.how_to && (
                  <div className="recommendation-how-to">
                    <div className="how-to-location">
                      📍 {rec.how_to.location}
                    </div>
                    {rec.how_to.steps && rec.how_to.steps.length > 0 && (
                      <ol className="how-to-steps">
                        {rec.how_to.steps.map((step, stepIndex) => (
                          <li key={stepIndex}>{step}</li>
                        ))}
                      </ol>
                    )}
                    {rec.direct_link && (
                      <button
                        className="direct-link-button"
                        onClick={() => handleDirectLink(rec.direct_link)}
                      >
                        Go to {rec.how_to.location} →
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Content */}
      <div style={{ marginTop: "1rem" }}>
        <h3 style={{ color: "#f3f4f6", marginBottom: "1rem" }}>
          Document Preview: {entityName}
        </h3>
        {documentData ? (
          renderHighlightedContent(documentData)
        ) : (
          <div style={{ color: "#9ca3af", padding: "2rem", textAlign: "center" }}>
            No document content available
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentPreview;
